import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { Modal } from '../ui/Modal.jsx';
import { Input } from '../ui/Input.jsx';
import { Select } from '../ui/Select.jsx';
import { Button } from '../ui/Button.jsx';

const KIND_OPTIONS = [
  { value: 'chat', label: 'Chat' },
  { value: 'realtime', label: 'Realtime (voce)' },
  { value: 'embedding', label: 'Embedding (RAG)' },
  { value: 'transcription', label: 'Trascrizione' }
];

const EMPTY = { id: '', label: '', kind: 'chat', enabled: true, pricing: {} };

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Add or edit a single catalog model. Pricing fields adapt to the kind. */
export function ModelFormModal({ open, onClose, initial, existingIds = [], onSave }) {
  const editing = Boolean(initial);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...EMPTY, ...initial, pricing: initial.pricing || {} } : EMPTY);
  }, [open, initial]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPrice = (k, v) => setForm((f) => ({ ...f, pricing: { ...f.pricing, [k]: v } }));

  const submit = () => {
    const id = form.id.trim();
    if (!id) return toast.error('Id modello richiesto.');
    if (!/^[a-zA-Z0-9._-]+$/.test(id)) return toast.error('Id non valido (lettere, numeri, . _ -).');
    if (!editing && existingIds.includes(id)) return toast.error('Esiste gia un modello con questo id.');
    if (!form.label.trim()) return toast.error('Etichetta richiesta.');

    const pricing = {};
    if (form.kind === 'chat' || form.kind === 'embedding') {
      if (num(form.pricing.inputPerMillion) !== undefined) pricing.inputPerMillion = num(form.pricing.inputPerMillion);
      if (num(form.pricing.outputPerMillion) !== undefined) pricing.outputPerMillion = num(form.pricing.outputPerMillion);
    } else if (form.kind === 'realtime') {
      ['inputTextPerMillionUsd', 'outputTextPerMillionUsd', 'inputAudioPerMillionUsd', 'outputAudioPerMillionUsd', 'cachedInputTextPerMillionUsd', 'cachedInputAudioPerMillionUsd'].forEach((k) => {
        if (num(form.pricing[k]) !== undefined) pricing[k] = num(form.pricing[k]);
      });
    }

    onSave({ id, label: form.label.trim(), kind: form.kind, enabled: form.enabled !== false, pricing });
  };

  const isText = form.kind === 'chat' || form.kind === 'embedding';
  const isRealtime = form.kind === 'realtime';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Modifica modello ${initial?.id}` : 'Nuovo modello'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={submit}>Salva</Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input label="Id modello" placeholder="es. gpt-4.1" disabled={editing} value={form.id} onChange={(e) => setField('id', e.target.value)} />
        <Input label="Etichetta" placeholder="es. GPT-4.1" value={form.label} onChange={(e) => setField('label', e.target.value)} />
        <Select label="Tipo" options={KIND_OPTIONS} value={form.kind} onChange={(e) => setField('kind', e.target.value)} />
        <label className="flex items-end gap-2 pb-2 text-sm text-ink">
          <input type="checkbox" className="h-4 w-4 accent-ink" checked={form.enabled !== false} onChange={(e) => setField('enabled', e.target.checked)} />
          Abilitato
        </label>

        {isText && (
          <>
            <Input label="Prezzo input (USD / 1M token)" type="number" step="0.01" min="0" value={form.pricing.inputPerMillion ?? ''} onChange={(e) => setPrice('inputPerMillion', e.target.value)} />
            <Input label="Prezzo output (USD / 1M token)" type="number" step="0.01" min="0" value={form.pricing.outputPerMillion ?? ''} onChange={(e) => setPrice('outputPerMillion', e.target.value)} />
          </>
        )}
        {isRealtime && (
          <>
            <Input label="Audio input (USD / 1M token)" type="number" step="0.01" min="0" value={form.pricing.inputAudioPerMillionUsd ?? ''} onChange={(e) => setPrice('inputAudioPerMillionUsd', e.target.value)} />
            <Input label="Audio output (USD / 1M token)" type="number" step="0.01" min="0" value={form.pricing.outputAudioPerMillionUsd ?? ''} onChange={(e) => setPrice('outputAudioPerMillionUsd', e.target.value)} />
            <Input label="Testo input (USD / 1M token)" type="number" step="0.01" min="0" value={form.pricing.inputTextPerMillionUsd ?? ''} onChange={(e) => setPrice('inputTextPerMillionUsd', e.target.value)} />
            <Input label="Testo output (USD / 1M token)" type="number" step="0.01" min="0" value={form.pricing.outputTextPerMillionUsd ?? ''} onChange={(e) => setPrice('outputTextPerMillionUsd', e.target.value)} />
            <Input label="Audio input cached (USD / 1M)" type="number" step="0.01" min="0" value={form.pricing.cachedInputAudioPerMillionUsd ?? ''} onChange={(e) => setPrice('cachedInputAudioPerMillionUsd', e.target.value)} />
            <Input label="Testo input cached (USD / 1M)" type="number" step="0.01" min="0" value={form.pricing.cachedInputTextPerMillionUsd ?? ''} onChange={(e) => setPrice('cachedInputTextPerMillionUsd', e.target.value)} />
          </>
        )}
        {form.kind === 'transcription' && (
          <p className="text-xs text-ink-muted sm:col-span-2">La trascrizione non incide sul calcolo costi attuale.</p>
        )}
      </div>
    </Modal>
  );
}
